import { useQueryBuilder } from "../../useBuilders";
import {
  ASSET_ANALYTICS,
  INTERACTIONS,
  JOURNEY,
  METRICS,
  PAGE_ANALYTICS,
  PAGES,
  PREVIEW,
  REPORTS,
  SESSIONS,
  SUMMARY,
  TIME_SERIES,
  TREND,
} from "../../queryKeys";
import { reportsApi } from "src/services/experiences/leadsAndReports/reports";
import { ReportRequest, ReportType } from "src/types/experiences/leadsAndReports";

const FIVE_MIN = 5 * 60 * 1000;

const rt = (request: ReportRequest): ReportType | string => request.reportType ?? "ALL";

// R1
export const useReportSummaryQuery = (storyboardId: string, request: ReportRequest, enabled = true) =>
  useQueryBuilder(
    [REPORTS, SUMMARY, storyboardId, rt(request)],
    () => reportsApi.fetchSummary(request),
    false,
    FIVE_MIN,
    false,
    undefined,
    enabled && Boolean(storyboardId),
    1,
  );

// R2
export const useSessionsQuery = (request: ReportRequest, key: string, page: number, enabled = true) =>
  useQueryBuilder(
    [REPORTS, SESSIONS, request.storyboard, rt(request), key, String(page)],
    () => reportsApi.fetchSessions(request, page, 5),
    false,
    FIVE_MIN,
    false,
    undefined,
    enabled && Boolean(request.storyboard),
    1,
  );

// R3
export const useSessionsTimeSeriesQuery = (request: ReportRequest, key: string, enabled = true) =>
  useQueryBuilder(
    [REPORTS, SESSIONS, TIME_SERIES, request.storyboard, rt(request), key],
    () => reportsApi.fetchSessionsTimeSeries(request),
    false,
    FIVE_MIN,
    false,
    undefined,
    enabled && Boolean(request.storyboard),
    1,
  );

// R5
export const useInteractionsQuery = (request: ReportRequest, key: string, page: number, enabled = true) =>
  useQueryBuilder(
    [REPORTS, INTERACTIONS, request.storyboard, rt(request), key, String(page)],
    () => reportsApi.fetchInteractions(request, page, 5),
    false,
    FIVE_MIN,
    false,
    undefined,
    enabled && Boolean(request.storyboard),
    1,
  );

// R6
export const useLeadsTimeSeriesQuery = (request: ReportRequest, key: string, enabled = true) =>
  useQueryBuilder(
    [REPORTS, "LEADS", TIME_SERIES, request.storyboard, rt(request), key],
    () => reportsApi.fetchLeadsTimeSeries(request),
    false,
    FIVE_MIN,
    false,
    undefined,
    enabled && Boolean(request.storyboard),
    1,
  );

// R7
export const useMetricsQuery = (
  metricType: string,
  request: ReportRequest,
  key: string,
  page: number,
  enabled = true,
) =>
  useQueryBuilder(
    [REPORTS, METRICS, metricType, request.storyboard, rt(request), key, String(page)],
    () => reportsApi.fetchMetrics({ ...request, metricType }, page, 5),
    false,
    FIVE_MIN,
    false,
    undefined,
    enabled && Boolean(request.storyboard),
    1,
  );

// R7b — single lead's metrics record (for custom fields on a deep-link / refresh)
export const useLeadRecordQuery = (
  profileId: string,
  request: ReportRequest,
  key: string,
  enabled = true,
) =>
  useQueryBuilder(
    [REPORTS, METRICS, "leadRecord", request.storyboard, rt(request), key, profileId],
    () => reportsApi.fetchLeadRecord(request, profileId),
    false,
    FIVE_MIN,
    false,
    undefined,
    enabled && Boolean(request.storyboard) && Boolean(profileId),
    1,
  );

// R7 (chart variant)
export const useMetricsChartQuery = (
  metricType: string,
  request: ReportRequest,
  key: string,
  enabled = true,
) =>
  useQueryBuilder(
    [REPORTS, METRICS, TREND, metricType, request.storyboard, rt(request), key],
    () => reportsApi.fetchMetrics({ ...request, metricType }, 0, 6),
    false,
    FIVE_MIN,
    false,
    undefined,
    enabled && Boolean(request.storyboard),
    1,
  );

// R9
export const useAssetAnalyticsQuery = (request: ReportRequest, key: string, page: number, enabled = true) =>
  useQueryBuilder(
    [REPORTS, ASSET_ANALYTICS, request.storyboard, rt(request), key, String(page)],
    () => reportsApi.fetchAssetAnalytics(request, page, 5),
    false,
    FIVE_MIN,
    false,
    undefined,
    enabled && Boolean(request.storyboard),
    1,
  );

// R10 — per-row asset trend
export const useAssetTrendQuery = (
  assetMappingId: string | null,
  request: { assetMappingId: string; contact?: string } & Record<string, unknown>,
  key: string,
  enabled = true,
) =>
  useQueryBuilder(
    [REPORTS, ASSET_ANALYTICS, TREND, assetMappingId ?? "none", request.contact ?? "all", key],
    () => reportsApi.fetchAssetTrend(request),
    false,
    FIVE_MIN,
    false,
    undefined,
    enabled && Boolean(assetMappingId),
    1,
  );

// R11 — document preview (dependent on contentType DOCUMENT)
export const useDocumentPreviewQuery = (assetId: string | null, enabled = false) =>
  useQueryBuilder(
    [REPORTS, ASSET_ANALYTICS, PREVIEW, assetId ?? "none"],
    () => reportsApi.fetchDocumentPreview(assetId ?? ""),
    false,
    FIVE_MIN,
    false,
    undefined,
    enabled && Boolean(assetId),
    1,
  );

// R14
export const useJourneyReportsQuery = (request: ReportRequest, key: string, page: number, enabled = true) =>
  useQueryBuilder(
    [REPORTS, JOURNEY, request.storyboard, rt(request), key, String(page)],
    () => reportsApi.fetchJourney(request, page, 5),
    false,
    FIVE_MIN,
    false,
    undefined,
    enabled && Boolean(request.storyboard),
    1,
  );

// R15/R17 — page analytics
export const usePageAnalyticsQuery = (
  variant: "interactive" | "landing",
  request: ReportRequest,
  key: string,
  page: number,
  enabled = true,
) =>
  useQueryBuilder(
    [REPORTS, PAGE_ANALYTICS, variant, request.storyboard, rt(request), key, String(page)],
    () => reportsApi.fetchPageAnalytics(request, variant, page, 5),
    false,
    FIVE_MIN,
    false,
    undefined,
    enabled && Boolean(request.storyboard),
    1,
  );

export const usePageAnalyticsChartQuery = (
  variant: "interactive" | "landing",
  request: ReportRequest,
  key: string,
  enabled = true,
) =>
  useQueryBuilder(
    [REPORTS, PAGE_ANALYTICS, TREND, variant, request.storyboard, rt(request), key],
    () => reportsApi.fetchPageAnalytics(request, variant, 0, 6),
    false,
    FIVE_MIN,
    false,
    undefined,
    enabled && Boolean(request.storyboard),
    1,
  );

export const useLandingPagesQuery = (storyboardId: string, enabled = true) =>
  useQueryBuilder(
    [REPORTS, PAGES, "landing", storyboardId],
    async () => {
      const response = await reportsApi.fetchAllLandingPages(storyboardId);
      return response?.records ?? [];
    },
    false,
    FIVE_MIN,
    false,
    undefined,
    enabled && Boolean(storyboardId),
    1,
  );

export const useRowTrendQuery = (
  variant: "interactive" | "landing",
  type: "sessions" | "country",
  rowKey: string | null,
  request: Record<string, unknown>,
  cacheKey: string,
  enabled = true,
  pages: any[] = [],
) =>
  useQueryBuilder(
    [REPORTS, type, TREND, variant, rowKey ?? "none", cacheKey],
    () => reportsApi.fetchRowTrend(request, variant, pages),
    false,
    FIVE_MIN,
    false,
    undefined,
    enabled && Boolean(rowKey),
    1,
  );
